import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, BarChart3, Globe, Palette, PenTool, Settings2, Sparkles, Clock } from 'lucide-react';

const ProductFeaturesBenefits = () => {
 const { t } = useTranslation();

 const features = [
 {
 id: 1,
 icon: <Bot className="w-6 h-6" />,
 emoji: '🤖',
 titleKey: 'productFeaturesBenefits.features.aiModels.title',
 itemsKey: 'productFeaturesBenefits.features.aiModels.items',
 gradient: 'from-accent-500 to-accent-400'
 },
 {
 id: 2,
 icon: <BarChart3 className="w-6 h-6" />,
 emoji: '📊',
 titleKey: 'productFeaturesBenefits.features.dataAnalysis.title',
 itemsKey: 'productFeaturesBenefits.features.dataAnalysis.items',
 gradient: 'from-green-500 to-green-600'
 },
 {
 id: 3,
 icon: <Globe className="w-6 h-6" />,
 emoji: '🌐',
 titleKey: 'productFeaturesBenefits.features.webBrowsing.title',
 itemsKey: 'productFeaturesBenefits.features.webBrowsing.items',
 gradient: 'from-accent-500 to-accent-600'
 },
 {
 id: 4,
 icon: <Palette className="w-6 h-6" />,
 emoji: '🎨',
 titleKey: 'productFeaturesBenefits.features.visualAI.title',
 itemsKey: 'productFeaturesBenefits.features.visualAI.items',
 gradient: 'from-accent-400 to-accent-500'
 },
 {
 id: 5,
 icon: <PenTool className="w-6 h-6" />,
 emoji: '📝',
 titleKey: 'productFeaturesBenefits.features.writing.title',
 itemsKey: 'productFeaturesBenefits.features.writing.items',
 gradient: 'from-accent-500 to-accent-600'
 },
 {
 id: 6,
 icon: <Settings2 className="w-6 h-6" />,
 emoji: '🔧',
 titleKey: 'productFeaturesBenefits.features.customGPTs.title',
 itemsKey: 'productFeaturesBenefits.features.customGPTs.items',
 gradient: 'from-accent-500 to-accent-600'
 }
 ];

 return (
 <section className="py-20 lg:py-28 bg-gradient-to-br from-cream-50 via-cream-50 to-cream-100 relative overflow-hidden">
 {/* 装饰性背景元素 */}
 <div className="absolute -top-20 -right-20 w-80 h-80 bg-accent-100 rounded-full opacity-20 blur-3xl"></div>
 <div className="absolute top-40 -left-32 w-64 h-64 bg-accent-100 rounded-full opacity-15 blur-2xl"></div>

 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
 {/* 标题部分 */}
 <div className="text-center mb-16">
 <div className="inline-flex items-center bg-accent-100 text-accent-600 rounded-full px-6 py-3 text-sm font-semibold mb-8">
 <Sparkles className="w-4 h-4 mr-2" />
 {t('productFeaturesBenefits.badge')}
 </div>
 <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-ink-900 mb-6 leading-tight">
 {t('productFeaturesBenefits.headline')}
 </h2>
 <p className="text-lg sm:text-xl text-ink-600 max-w-3xl mx-auto leading-relaxed">
 {t('productFeaturesBenefits.subtitle')}
 </p>
 </div>

 {/* 功能网格 */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
 {features.map((feature) => (
 <div
 key={feature.id}
 className="bg-cream-50 rounded-xl border border-ink-100 p-6 hover:shadow-md transition-all duration-300 group relative overflow-hidden h-full flex flex-col"
 >
 {/* 装饰性背景 */}
 <div className={`absolute -top-16 -right-16 w-32 h-32 bg-gradient-to-r ${feature.gradient} rounded-full opacity-5 blur-2xl group-hover:opacity-10 transition-opacity duration-300`}></div>
 
 <div className="relative z-10 flex flex-col h-full">
 {/* 图标和标题 */}
 <div className="flex items-center gap-3 mb-5">
 <div className={`w-12 h-12 bg-gradient-to-r ${feature.gradient} rounded-xl flex items-center justify-center text-white shadow-lg transition-transform duration-300`}>
 {feature.icon}
 </div>
 <div className="flex items-center gap-2">
 <span className="text-xl">{feature.emoji}</span>
 <h3 className="text-lg font-bold text-ink-900 leading-tight">
 {t(feature.titleKey)}
 </h3>
 </div>
 </div>

 {/* 功能列表 */}
 <div className="space-y-2.5 flex-grow">
 {t(feature.itemsKey, { returnObjects: true }).map((item, index) => (
 <div key={index} className="flex items-start gap-2.5">
 <div className={`w-4 h-4 bg-gradient-to-r ${feature.gradient} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
 <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
 </div>
 <span className="text-ink-700 text-sm leading-relaxed">{item}</span>
 </div>
 ))}
 </div>
 </div>
 </div>
 ))}
 </div>

 {/* 底部总结 */}
 <div className="mt-16 text-center">
 <div className="bg-gradient-to-r from-accent-50 to-accent-50 rounded-xl p-8 border border-accent-100">
 <div className="flex items-center justify-center gap-3 mb-4">
 <Clock className="w-6 h-6 text-accent-600" />
 <h3 className="text-xl font-bold text-ink-900">
 {t('productFeaturesBenefits.summary.title')}
 </h3>
 </div>
 <p className="text-ink-700 leading-relaxed max-w-3xl mx-auto">
 {t('productFeaturesBenefits.summary.description')}
 </p>
 </div>
 </div>
 </div>
 </section>
 );
};

export default ProductFeaturesBenefits;